import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import { getFileUrl } from '../utils/fileUtil';
import KnowledgeFileEditor from '../components/common/KnowledgeFileEditor';

const Teams = () => {
  const { token, currentUser } = useContext(AuthContext);
  const location = useLocation();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(null); // workspaceId
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  
  // Existing users selection state
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Knowledge files state
  const [expandedKnowledge, setExpandedKnowledge] = useState({}); // { wsId: bool }
  const [editingKnowledgeFile, setEditingKnowledgeFile] = useState(null); // { file, wsId }

  useEffect(() => {
    fetchWorkspaces();
    fetchAllUsers();

    // Check for ?create=true in URL
    const params = new URLSearchParams(location.search);
    if (params.get('create') === 'true') {
      setShowCreateModal(true);
    }
  }, [location.search]);

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get('/api/workspaces', {
        headers: { 'x-auth-token': token }
      });
      setWorkspaces(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get('/api/users', {
        headers: { 'x-auth-token': token }
      });
      setAllUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/workspaces', { name: newWorkspaceName }, {
        headers: { 'x-auth-token': token }
      });
      setNewWorkspaceName('');
      setShowCreateModal(false);
      fetchWorkspaces();
    } catch (err) {
      alert('Failed to create team');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const workspaceId = showInviteModal;
      
      // We check if we're adding by selected ID or by email
      const payload = selectedUserId ? { userId: selectedUserId } : { email: inviteEmail };
      
      // The current addMember endpoint in workspaceController only takes userId. 
      // We might need to handle the email lookup on backend if we want true "invite by email"
      // For now, let's focus on the user's request: "select from all users"
      
      if (!selectedUserId && !inviteEmail) return;

      await axios.post(`/api/workspaces/${workspaceId}/members`, payload, {
        headers: { 'x-auth-token': token }
      });
      
      setInviteEmail('');
      setSelectedUserId('');
      setShowInviteModal(null);
      fetchWorkspaces();
      alert('Member added!');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to add user');
    }
  };

  const handleCreateWsKnowledgeFile = async (wsId) => {
    try {
      const res = await axios.post(`/api/workspaces/${wsId}/knowledge`, { name: 'untitled.md', content: '' }, { headers: { 'x-auth-token': token } });
      setWorkspaces(prev => prev.map(ws => ws._id === wsId ? { ...ws, knowledgeFiles: [...(ws.knowledgeFiles || []), res.data] } : ws));
      setEditingKnowledgeFile({ file: res.data, wsId });
    } catch (err) {
      console.error('Error creating workspace knowledge file:', err);
    }
  };

  const handleSaveWsKnowledgeFile = async (fileId, name, content) => {
    const wsId = editingKnowledgeFile?.wsId;
    if (!wsId) return;
    const res = await axios.put(`/api/workspaces/${wsId}/knowledge/${fileId}`, { name, content }, { headers: { 'x-auth-token': token } });
    setWorkspaces(prev => prev.map(ws => ws._id === wsId
      ? { ...ws, knowledgeFiles: ws.knowledgeFiles.map(f => f._id === fileId ? { ...f, name: res.data.name, content: res.data.content } : f) }
      : ws
    ));
    setEditingKnowledgeFile(prev => prev ? { ...prev, file: { ...prev.file, name: res.data.name, content: res.data.content } } : prev);
  };

  const handleDeleteWsKnowledgeFile = async (wsId, fileId) => {
    if (!window.confirm('Delete this knowledge file?')) return;
    await axios.delete(`/api/workspaces/${wsId}/knowledge/${fileId}`, { headers: { 'x-auth-token': token } });
    setWorkspaces(prev => prev.map(ws => ws._id === wsId ? { ...ws, knowledgeFiles: ws.knowledgeFiles.filter(f => f._id !== fileId) } : ws));
    if (editingKnowledgeFile?.file?._id === fileId) setEditingKnowledgeFile(null);
  };

  const isWsAdmin = (ws) => {
    return ws.owner?._id === currentUser?._id || ws.owner === currentUser?._id ||
      ws.members?.some(m => (m.user?._id || m.user) === currentUser?._id && m.role === 'admin');
  };

  if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading your teams...</div>;

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 font-sans text-gray-900 dark:text-gray-100">
      <div className="flex justify-between items-center mb-8 text-gray-900 dark:text-gray-100">
        <h1 className="text-3xl font-black uppercase tracking-tight">Your Teams</h1>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-blue text-white rounded-xl hover:bg-blue-700 font-bold transition-all shadow-lg"
        >
          + Create Team
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {workspaces.map(ws => (
          <div key={ws._id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
              <h2 className="text-xl font-black text-gray-800 dark:text-gray-100 uppercase tracking-tight">{ws.name}</h2>
              <span className="text-[10px] font-black bg-primary-blue/10 dark:bg-blue-950/40 text-primary-blue dark:text-blue-400 px-3 py-1 rounded-full uppercase tracking-widest">
                {ws.members.length} Members
              </span>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4">Active Roster</h3>
                <div className="space-y-4">
                  {ws.members.filter(m => m.user).map(member => (
                    <div key={member.user._id} className="flex items-center justify-between group">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-secondary-cyan flex items-center justify-center text-white font-black border-2 border-white dark:border-gray-800 shadow-sm overflow-hidden transition-transform group-hover:scale-110">
                          {member.user.avatar ? (
                            <img src={getFileUrl(member.user.avatar)} alt="" className="h-full w-full object-cover" />
                          ) : member.user.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center">
                            <p className="text-sm font-black text-gray-900 dark:text-gray-100 leading-none uppercase tracking-tighter">{member.user.name}</p>
                            {member.user.isAgent && <span className="ml-2 text-[8px] bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 px-1 rounded font-black">🤖</span>}
                          </div>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold mt-1 tracking-tight">{member.user.email}</p>
                        </div>
                      </div>
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-lg uppercase tracking-tighter ${member.role === 'admin' ? 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400'}`}>
                        {member.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Knowledge Files Section */}
              <div className="mb-4">
                <button
                  onClick={() => setExpandedKnowledge(prev => ({ ...prev, [ws._id]: !prev[ws._id] }))}
                  className="flex items-center justify-between w-full text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-2 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span>Knowledge Files</span>
                  <span className="text-[10px]">{expandedKnowledge[ws._id] ? '▲' : '▼'}</span>
                </button>
                {expandedKnowledge[ws._id] && (
                  <div className="space-y-1">
                    {(ws.knowledgeFiles || []).length === 0 ? (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">No knowledge files yet.</p>
                    ) : (
                      (ws.knowledgeFiles || []).map(file => (
                        <div key={file._id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-750 group">
                          <button
                            onClick={() => setEditingKnowledgeFile({ file, wsId: ws._id })}
                            className="flex items-center gap-2 text-left flex-1 min-w-0"
                          >
                            <span className="text-gray-400">📄</span>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                          </button>
                          {isWsAdmin(ws) && (
                            <button
                              onClick={() => handleDeleteWsKnowledgeFile(ws._id, file._id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs ml-2 transition-opacity"
                              title="Delete file"
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      ))
                    )}
                    {isWsAdmin(ws) && (
                      <button
                        onClick={() => handleCreateWsKnowledgeFile(ws._id)}
                        className="text-[10px] font-black text-primary-blue dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 uppercase tracking-wide mt-1"
                      >
                        + Add File
                      </button>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowInviteModal(ws._id)}
                className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest hover:border-primary-blue hover:text-primary-blue dark:hover:text-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-950/10 transition-all"
              >
                + Manage Team Members
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Knowledge File Side Panel */}
      {editingKnowledgeFile && (
        <KnowledgeFileEditor
          file={editingKnowledgeFile.file}
          onSave={handleSaveWsKnowledgeFile}
          onClose={() => setEditingKnowledgeFile(null)}
        />
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md animate-fade-in border border-gray-100 dark:border-gray-700">
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6 uppercase tracking-tight">Create New Workspace</h3>
            <form onSubmit={handleCreateWorkspace}>
              <input 
                autoFocus
                required
                type="text" 
                placeholder="Team Name (e.g. Creative Services)"
                className="w-full border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6 focus:ring-primary-blue font-bold text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
              />
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 dark:text-gray-500 font-black uppercase text-xs tracking-widest hover:text-gray-600 dark:hover:text-gray-300 px-4">Cancel</button>
                <button type="submit" className="px-8 py-3 bg-primary-blue text-white rounded-xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg">Create Workspace</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal (Updated with selection) */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md animate-fade-in border border-gray-100 dark:border-gray-700">
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 uppercase tracking-tight">Add Team Member</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest mb-6 italic">Select an existing user or agent</p>
            
            <form onSubmit={handleInvite} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-2">Search Existing Profiles</label>
                <select 
                  className="w-full border-gray-200 dark:border-gray-700 rounded-xl p-3 focus:ring-primary-blue font-bold text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    if (e.target.value) setInviteEmail('');
                  }}
                >
                  <option value="">-- Choose User or Agent --</option>
                  {allUsers.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.isAgent ? '🤖 ' : '👤 '}{user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100 dark:border-gray-700"></div></div>
                <div className="relative flex justify-center text-[10px] uppercase font-black text-gray-300 dark:text-gray-500 bg-white dark:bg-gray-800 px-2">or invite by email</div>
              </div>

              <div>
                <input 
                  type="email" 
                  placeholder="colleague@example.com"
                  className="w-full border-gray-200 dark:border-gray-700 rounded-xl p-3 focus:ring-primary-blue font-bold text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    if (e.target.value) setSelectedUserId('');
                  }}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { setShowInviteModal(null); setSelectedUserId(''); setInviteEmail(''); }} className="text-gray-400 dark:text-gray-550 font-black uppercase text-xs tracking-widest hover:text-gray-600 dark:hover:text-gray-300 px-4">Cancel</button>
                <button type="submit" className="px-8 py-3 bg-secondary-cyan text-white rounded-xl font-black uppercase tracking-widest hover:bg-cyan-600 transition-all shadow-lg">Add to Team</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;